import React from 'react';
import { Link, useParams } from 'react-router-dom';
import PlayerStatSheetView from '../../components/playerSheet/PlayerStatSheetView';
import { usePlayerSheetData } from '../../components/playerSheet/usePlayerSheetData';
import { PlayerSheetVariant } from '../../components/playerSheet/playerSheetTypes';
import { sessionManager } from '../../config/sessionManager';

interface PlayerStatSheetPageProps {
  shareContext?: {
    clientId: string;
    seasonId: string;
    playerSeasonId: string;
  };
  readOnly?: boolean;
  variant?: PlayerSheetVariant;
}

const PlayerStatSheetPage: React.FC<PlayerStatSheetPageProps> = ({
  shareContext,
  readOnly = false,
  variant = 'season',
}) => {
  const params = useParams<{ seasonId?: string; playerSeasonId?: string; playerId?: string }>();
  const clientId = shareContext?.clientId || sessionManager.getClientId();
  const { data, loading, error } = usePlayerSheetData(
    {
      variant,
      clientId: clientId || undefined,
      seasonId: shareContext?.seasonId || params.seasonId || sessionManager.getSeasonId() || undefined,
      playerSeasonId: shareContext?.playerSeasonId || params.playerSeasonId,
      playerId: params.playerId,
    },
    readOnly,
  );

  if (loading) {
    return (
      <div className="w-full py-16 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#05396b]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="bg-white border border-[#bccedf] rounded-lg p-8 text-center">
          <p className="text-red-600 mb-4">{error || 'Player not found'}</p>
          {!readOnly && (
            <Link
              to={variant === 'career' ? '/players' : params.seasonId ? `/seasons/${params.seasonId}/players` : '/players'}
              className="text-[#05396b] underline font-medium"
            >
              Back to Players
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
      {!readOnly && (
        <Link
          to={variant === 'career' ? '/players' : params.seasonId ? `/seasons/${params.seasonId}/players` : '/players'}
          className="text-sm text-[#05396b] hover:underline font-medium inline-block mb-3"
        >
          {variant === 'career' ? '← Players' : '← Players Report'}
        </Link>
      )}
      <PlayerStatSheetView
        data={data}
        variant={variant}
        readOnly={readOnly}
        clientId={clientId}
        initialSeasonId={params.seasonId || sessionManager.getSeasonId() || undefined}
      />
    </div>
  );
};

export default PlayerStatSheetPage;
